import { useState } from 'react';
import { useForm } from 'react-hook-form';
import styles from './PatientLinksModal.module.scss';
import { Button, ErrorModalContent, Modal, Textarea } from 'components';
import { selectModalsErrors } from 'store/app/selectors';
import { appActions } from 'store/app/slice';
import { selectCurrentPatientCase } from 'store/patients/selectors';
import { patientsActions } from 'store/patients/slice';
import { useDispatch, useSelector } from 'store/utils';
import { getInputRowsCount, transformArrayToStringValue, transformStringToArrayByNewLine } from 'utils';

export const PatientLinksModal = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const [displayError, setDisplayError] = useState(false);

  const { scan3dLinks, letterLinks, otherLinks, id } = useSelector(selectCurrentPatientCase) ?? {};
  const errorMessage = useSelector(selectModalsErrors).patientLinksError;

  const scan3dLinksString = transformArrayToStringValue(scan3dLinks);
  const letterLinksString = transformArrayToStringValue(letterLinks);
  const otherLinksString = transformArrayToStringValue(otherLinks);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      scan3dLinks: scan3dLinksString,
      letterLinks: letterLinksString,
      otherLinks: otherLinksString,
    },
  });

  const close = () => {
    dispatch(appActions.setIsModalOpened({ name: 'addPatientLinks', isOpened: false }));
  };

  const onSubmit = handleSubmit(async (formData) => {
    if (isLoading) return;

    const { scan3dLinks, letterLinks, otherLinks } = formData;
    const scan3dLinksArray = transformStringToArrayByNewLine(scan3dLinks);
    const letterLinksArray = transformStringToArrayByNewLine(letterLinks);
    const otherLinksArray = transformStringToArrayByNewLine(otherLinks);
    try {
      setIsLoading(true);
      const wrappedResult = dispatch(
        patientsActions.updatePatientCase({
          patientCaseData: {
            scan3dLinks: scan3dLinksArray,
            letterLinks: letterLinksArray,
            otherLinks: otherLinksArray,
          },
          patientCaseId: id as number,
        }),
      );
      await wrappedResult.unwrap();
      close();
      dispatch(appActions.setModalErrorText({ name: 'patientLinksError', errorText: '' }));
    } catch (error: any) {
      dispatch(appActions.setModalErrorText({ name: 'patientLinksError', errorText: error?.title }));
      setDisplayError(true);
    } finally {
      setIsLoading(false);
    }
  });

  return (
    <Modal onClose={close} title="Links">
      {displayError ? (
        <ErrorModalContent
          onConfirm={close}
          title="Links are not updated"
          subtitle={`Updates to the links were not saved${errorMessage ? `. ${errorMessage}` : ' due to technical errors'}`}
          contentContainerClassname={styles.errorContentContainer}
        />
      ) : (
        <div className={styles.formContainer}>
          <form className={styles.form}>
            <Textarea
              inputName="scan3dLinks"
              labelText="3dScan"
              placeholder="Please provide 3d scan links"
              register={register}
              rowsValue={getInputRowsCount(scan3dLinksString)}
            />
            <Textarea
              inputName="letterLinks"
              labelText="Letter"
              placeholder="Please provide letter links"
              register={register}
              rowsValue={getInputRowsCount(letterLinksString)}
            />
            <Textarea
              inputName="otherLinks"
              labelText="Other"
              placeholder="Please provide other links"
              register={register}
              rowsValue={getInputRowsCount(otherLinksString)}
              errorMsg={errors.otherLinks?.message as string}
            />
          </form>
          <div className={styles.buttonRow}>
            <Button isLink onClick={close}>
              Cancel
            </Button>
            <Button onClick={onSubmit}>Save</Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
